// ────────────────────────────────────────────────────────────────────────
// SweaterQuestionnaire — Feb 2026.
// Parallel to CasualShirtsQuestionnaire. Opens from the shirts tab's
// "Sweaters" subcategory header (sits directly under Casual Shirts). The
// tailor picks (1) knitwear style, (2) sweater colour, (3) pant colour,
// (4) shoe style + colour. The submit callback returns a combo object
// shaped like the SHIRT_SUBCATEGORIES entries so Dashboard drops it
// straight into the existing generateComboLooks() flow with
// `category: 'shirts_sweaters'` — no new backend endpoint required. The
// backend's new "sweaters" base_category picks up the render.
// ────────────────────────────────────────────────────────────────────────
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Sparkles } from 'lucide-react';
import { PANT_COLORS as _PANT_COLORS_RAW } from './CustomizeModal';
import TrouserRestructureBlock, {
  buildTrouserStyleDescription,
  buildTrouserStyleTokens,
} from './TrouserRestructureBlock';

// CustomizeModal exports pants as { value: hex, name }. Normalise to
// the { id, hex, label } shape the rest of this component expects.
const PANT_COLOURS = _PANT_COLORS_RAW.map(p => ({ id: p.name, hex: p.value, label: p.name }));

// ── Style catalogue (9 knitwear silhouettes) ────────────────────────
// Each entry produces a fabric_note that fully describes the knitwear
// piece so Gemini renders the exact silhouette the tailor picked.
// Placeholders {sweater}/{sweaterHex}/{pant}/{pantHex}/{shoe}/{shoeColour}
// are filled in at submit time.
const STYLES = [
  {
    id: 'crewneck_fine_gauge',
    label: 'Fine-Gauge Crewneck',
    desc: 'Merino crewneck · slim editorial fit',
    build: ({ sweater, pant, pantHex, shoe, shoeColour }) => (
      `refined ${sweater.toLowerCase()} FINE-GAUGE MERINO WOOL crewneck sweater — clean ribbed crew neckline sitting flat against the base of the throat, ribbed cuffs at the wrist, ribbed hem sitting at the natural waistband, slim tailored fit that skims the torso (never oversized, never boxy). Long sleeves ending at the wrist bone. Worn ALONE against the skin — no shirt collar peeking out, no undershirt visible at the neckline. Paired with ${pant.toLowerCase()} wool flannel trousers (${pantHex}) with brown leather belt, ${shoeColour.toLowerCase()} ${shoe} shoes. Smart-casual editorial magazine aesthetic. Knit stitch texture clearly readable at chest and cuffs.`
    ),
  },
  {
    id: 'vneck_fine_gauge',
    label: 'Fine-Gauge V-Neck',
    desc: 'Merino V-neck · classic silhouette',
    build: ({ sweater, pant, pantHex, shoe, shoeColour }) => (
      `refined ${sweater.toLowerCase()} FINE-GAUGE MERINO WOOL V-NECK sweater — shallow clean V neckline (not too deep), ribbed cuffs, ribbed hem, slim tailored fit. Worn OVER a crisp white cotton Oxford shirt with the shirt collar and cuffs neatly visible above the V and at the sleeve ends. Paired with ${pant.toLowerCase()} wool trousers (${pantHex}) with brown leather belt, ${shoeColour.toLowerCase()} ${shoe} shoes. Smart-casual editorial aesthetic. Fine knit stitch texture visible.`
    ),
  },
  {
    id: 'mock_neck',
    label: 'Mock Neck',
    desc: 'Short stand collar · sleek modernist',
    build: ({ sweater, pant, pantHex, shoe, shoeColour }) => (
      `sleek ${sweater.toLowerCase()} FINE-GAUGE MERINO WOOL MOCK-NECK sweater — short stand collar sitting 3–4 cm above the collarbone (halfway between crewneck and full turtleneck, does NOT fold over), ribbed cuffs, ribbed hem, slim tailored fit. Worn ALONE against the skin — no shirt underneath. Paired with ${pant.toLowerCase()} wool trousers (${pantHex}) with brown leather belt, ${shoeColour.toLowerCase()} ${shoe} shoes. Smart-casual, minimalist Italian editorial aesthetic.`
    ),
  },
  {
    id: 'turtleneck_roll',
    label: 'Turtleneck / Roll-Neck',
    desc: 'Full fold-over collar · winter essential',
    build: ({ sweater, pant, pantHex, shoe, shoeColour }) => (
      `luxury ${sweater.toLowerCase()} CASHMERE or fine-gauge merino TURTLENECK / ROLL-NECK sweater — a full stand collar that FOLDS OVER on itself sitting neatly at the jawline (a proper roll-neck, NOT a mock neck), ribbed cuffs at the wrist, ribbed hem, tailored fit. Worn ALONE against the skin — no shirt underneath. Paired with ${pant.toLowerCase()} wool flannel trousers (${pantHex}) with brown leather belt, ${shoeColour.toLowerCase()} ${shoe} shoes. Sophisticated smart-casual editorial aesthetic — cashmere hand-feel visible in the drape of the collar fold.`
    ),
  },
  {
    id: 'cable_knit_crew',
    label: 'Cable-Knit Crewneck',
    desc: 'Chunky cable columns · heritage Aran',
    build: ({ sweater, pant, pantHex, shoe, shoeColour }) => (
      `heritage ${sweater.toLowerCase()} HAND-KNIT ARAN CABLE crewneck sweater — chunky raised CABLE COLUMNS running vertically down the front and sleeves (thick 3-4 cm cable twists clearly visible in relief, not flat), ribbed crew neck, ribbed cuffs, ribbed hem, relaxed but tailored fit (never oversized). Worn ALONE or with a crisp white Oxford shirt collar peeking above the ribbed crew (only if the fit is refined). Paired with ${pant.toLowerCase()} wool flannel trousers (${pantHex}) with leather belt, ${shoeColour.toLowerCase()} ${shoe} shoes. Rich cable stitch texture the hero of the shot. Editorial smart-casual aesthetic.`
    ),
  },
  {
    id: 'quarter_zip',
    label: 'Quarter-Zip',
    desc: 'Sporty stand collar · half-zip closure',
    build: ({ sweater, pant, pantHex, shoe, shoeColour }) => (
      `refined ${sweater.toLowerCase()} MEDIUM-GAUGE MERINO WOOL QUARTER-ZIP sweater — short stand collar with a metal or tonal zipper running roughly 15-20 cm from the neckline down (the zip is a real functional element, visible on the chest), ribbed cuffs, ribbed hem, slim tailored fit. Zipper is left about half-open showing a crisp white Oxford shirt collar underneath. Paired with ${pant.toLowerCase()} cotton chinos (${pantHex}) with brown leather belt, ${shoeColour.toLowerCase()} ${shoe} shoes. Smart-casual editorial aesthetic — country-club adjacent, never gym-wear.`
    ),
  },
  {
    id: 'shawl_cardigan',
    label: 'Shawl-Collar Cardigan',
    desc: 'Rolling shawl collar · button front',
    build: ({ sweater, pant, pantHex, shoe, shoeColour }) => (
      `luxury ${sweater.toLowerCase()} CHUNKY MERINO or CASHMERE SHAWL-COLLAR CARDIGAN — a rich rolling SHAWL COLLAR (continuous curved lapel-like collar, NOT notched, wrapping around the neck like a robe) with 4-5 real horn or leather-covered buttons running down the front (fastened at the middle button, top two open to show a shirt underneath), ribbed cuffs, ribbed hem, slightly relaxed tailored fit. Worn OVER a crisp white cotton Oxford shirt with the shirt collar visible above the shawl and cuffs visible at the wrist. Paired with ${pant.toLowerCase()} wool flannel trousers (${pantHex}) with brown leather belt, ${shoeColour.toLowerCase()} ${shoe} shoes. Rich knit texture, gentleman-at-home editorial aesthetic.`
    ),
  },
  {
    id: 'button_cardigan',
    label: 'Button-Front Cardigan',
    desc: 'V-neck cardigan · classic button front',
    build: ({ sweater, pant, pantHex, shoe, shoeColour }) => (
      `refined ${sweater.toLowerCase()} FINE-GAUGE MERINO WOOL V-NECK CARDIGAN — a clean V-neck opening with a placket running down the centre closed by 5-6 tonal buttons (fastened at the middle two buttons, top and bottom open), ribbed cuffs, ribbed hem, slim tailored fit. Worn OVER a crisp white cotton Oxford shirt with shirt collar visible at the neck and shirt cuffs visible at the wrists — a striped or knitted tie is optional (only include if the fabric_note explicitly says so). Paired with ${pant.toLowerCase()} wool trousers (${pantHex}) with brown leather belt, ${shoeColour.toLowerCase()} ${shoe} shoes. Smart-casual editorial aesthetic.`
    ),
  },
  {
    id: 'polo_collar_sweater',
    label: 'Polo-Collar Sweater',
    desc: 'Knit polo collar · 2-button placket',
    build: ({ sweater, pant, pantHex, shoe, shoeColour }) => (
      `refined ${sweater.toLowerCase()} FINE-GAUGE MERINO WOOL POLO-COLLAR sweater — a soft KNIT POLO COLLAR (spread flat, sits at the base of the throat, matches body colour) with a clean two-button placket in the same colour, LONG ribbed sleeves ending at the wrist, ribbed hem, slim tailored fit. Placket top button is open, second button fastened. Worn ALONE against the skin — no shirt underneath. Paired with ${pant.toLowerCase()} wool flannel trousers (${pantHex}) with brown leather belt, ${shoeColour.toLowerCase()} ${shoe} shoes. Sophisticated Italian editorial aesthetic — think 1960s Milan.`
    ),
  },
];

// Sweater-appropriate colour palette (single source of truth kept
// coherent with the app's shirt / suit palette). Muted heritage tones
// look best in knitwear; loud neons are excluded.
const SWEATER_COLOURS = [
  { id: 'Cream',           label: 'Cream',           hex: '#F5F0E1' },
  { id: 'Charcoal',        label: 'Charcoal',        hex: '#36454F' },
  { id: 'Navy',            label: 'Navy',            hex: '#1C2841' },
  { id: 'Camel',           label: 'Camel',           hex: '#C19A6B' },
  { id: 'Forest Green',    label: 'Forest Green',    hex: '#2F4F2F' },
  { id: 'Oxblood',         label: 'Oxblood',         hex: '#582430' },
  { id: 'Heather Grey',    label: 'Heather Grey',    hex: '#9CA3AF' },
  { id: 'Rust',            label: 'Rust',            hex: '#B7410E' },
  { id: 'Chocolate Brown', label: 'Chocolate Brown', hex: '#3C2415' },
  { id: 'Sage Green',      label: 'Sage Green',      hex: '#A7C8A5' },
];

const SHOE_STYLES = [
  { id: 'loafer',       label: 'Loafer' },
  { id: 'derby',        label: 'Derby' },
  { id: 'chelsea_boot', label: 'Chelsea Boot' },
  { id: 'monk',         label: 'Monk-Strap' },
  { id: 'sneaker',      label: 'Sneaker' },
  { id: 'oxford',       label: 'Oxford' },
];

const SHOE_COLOURS = [
  { id: 'Black',         label: 'Black',         hex: '#0A0A0C' },
  { id: 'Dark Brown',    label: 'Dark Brown',    hex: '#3A2418' },
  { id: 'Cognac',        label: 'Cognac',        hex: '#9B5E2E' },
  { id: 'Tan',           label: 'Tan',           hex: '#C19A6B' },
  { id: 'White',         label: 'White',         hex: '#F5F0E1' },
  { id: 'Oxblood',       label: 'Oxblood',       hex: '#582430' },
];

const _Section = ({ title, children, testid }) => (
  <div className="space-y-3" data-testid={testid}>
    <h4 className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent))] font-semibold">
      {title}
    </h4>
    {children}
  </div>
);

const _Chip = ({ selected, onClick, hex, label, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center gap-2 px-3 py-2 border text-[11px] uppercase tracking-wider transition-all ${selected
      ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[#F5F5F5]'
      : 'border-[#27272A] text-[#A1A1AA] hover:border-[#3F3F46]'}`}
    data-testid={testid}
  >
    {hex && <span className="w-4 h-4 border border-[#27272A]" style={{ backgroundColor: hex }} />}
    <span>{label}</span>
  </button>
);

export default function SweaterQuestionnaire({
  open, onClose, onSubmit,
  generating = false,
  hasClientImage = true,
}) {
  const [styleId,    setStyleId]    = useState(STYLES[0].id);
  const [sweaterId,  setSweaterId]  = useState(SWEATER_COLOURS[0].id);
  const [pantId,     setPantId]     = useState(PANT_COLOURS[0]?.id || 'Charcoal');
  const [shoeStyle,  setShoeStyle]  = useState('loafer');
  const [shoeColour, setShoeColour] = useState('Dark Brown');
  // Iter 169 — trouser restructure state (pleats/cuffs/waistband/belt).
  const [trPleats,     setTrPleats]     = useState(null);
  const [trCuffs,      setTrCuffs]      = useState(false);
  const [trWaist,      setTrWaist]      = useState(null);
  const [trBeltColor,  setTrBeltColor]  = useState(null);

  const handleSubmit = () => {
    const style   = STYLES.find(s => s.id === styleId) || STYLES[0];
    const sweater = SWEATER_COLOURS.find(c => c.id === sweaterId) || SWEATER_COLOURS[0];
    const pant    = PANT_COLOURS.find(c => c.id === pantId) || { id: 'Charcoal', hex: '#36454F', label: 'Charcoal' };
    const shoe    = SHOE_STYLES.find(s => s.id === shoeStyle) || SHOE_STYLES[0];
    const shoeCol = SHOE_COLOURS.find(c => c.id === shoeColour) || SHOE_COLOURS[0];

    const fabric_note = style.build({
      sweater:    sweater.label,
      sweaterHex: sweater.hex,
      pant:       pant.label,
      pantHex:    pant.hex,
      shoe:       shoe.label.toLowerCase(),
      shoeColour: shoeCol.label,
    }) + buildTrouserStyleDescription({ trPleats, trCuffs, trWaist, trBeltColor });

    onSubmit?.({
      combo: {
        // Include the colour in the garment name so two different
        // sweater colours in the same style don't share a dedup key
        // and overwrite each other in the gallery.
        garment: `${sweater.label} ${style.label}`,
        garment_id: 'sweater',
        style_id: style.id,
        color: sweater.label,
        suit: sweater.hex,
        pattern: 'solid',
        shirt: sweater.label,
        pant: pant.hex,
        shoe: shoe.id,
        shoeColor: shoeCol.label,
        label: `${sweater.label} ${style.label}`,
        fabric_note,
        // Iter 233 (Feb 2026) — send the composite `trouser_style`
        // token so the backend structural-only lock enforces the
        // waistband choice (side_tabs REPLACES belt loops).
        trouser_style: buildTrouserStyleTokens({ trPleats, trCuffs, trWaist, trBeltColor }) || undefined,
      },
      category: 'shirts_sweaters',
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-2xl max-h-[92vh] overflow-y-auto bg-[#0A0A0C] border-[#27272A] rounded-none"
        data-testid="sweater-questionnaire-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-light text-[#F5F5F5]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Sweater Builder
          </DialogTitle>
          <DialogDescription className="text-xs text-[#A1A1AA]">
            Pick a knitwear style, colours and shoes — we&apos;ll render one editorial look with your client.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pt-2">
          {/* ── Style ─────────────────────────────────────────── */}
          <_Section title="Style" testid="sweater-style-section">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {STYLES.map(s => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setStyleId(s.id)}
                  className={`text-left px-3 py-3 border transition-all ${styleId === s.id
                    ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10'
                    : 'border-[#27272A] hover:border-[#3F3F46]'}`}
                  data-testid={`sweater-style-${s.id}`}
                >
                  <p className="text-sm text-[#F5F5F5]">{s.label}</p>
                  <p className="text-[10px] text-[#A1A1AA] mt-0.5">{s.desc}</p>
                </button>
              ))}
            </div>
          </_Section>

          {/* ── Sweater colour ────────────────────────────────── */}
          <_Section title="Sweater Colour" testid="sweater-color-section">
            <div className="flex flex-wrap gap-2">
              {SWEATER_COLOURS.map(c => (
                <_Chip
                  key={c.id}
                  selected={sweaterId === c.id}
                  onClick={() => setSweaterId(c.id)}
                  hex={c.hex}
                  label={c.label}
                  testid={`sweater-color-${c.id.replace(/\s+/g, '-').toLowerCase()}`}
                />
              ))}
            </div>
          </_Section>

          {/* ── Pant colour ────────────────────────────────────── */}
          <_Section title="Trouser Colour" testid="sweater-pant-section">
            <div className="flex flex-wrap gap-2">
              {PANT_COLOURS.map(c => (
                <_Chip
                  key={c.id}
                  selected={pantId === c.id}
                  onClick={() => setPantId(c.id)}
                  hex={c.hex}
                  label={c.label}
                  testid={`sweater-pant-${c.id.replace(/\s+/g, '-').toLowerCase()}`}
                />
              ))}
            </div>
          </_Section>

          {/* ── Trouser Restructure (iter 169) ───────────────── */}
          <_Section title="Trouser Restructure" testid="sweater-trouser-restructure-section">
            <TrouserRestructureBlock
              trPleats={trPleats}       setTrPleats={setTrPleats}
              trCuffs={trCuffs}         setTrCuffs={setTrCuffs}
              trWaist={trWaist}         setTrWaist={setTrWaist}
              trBeltColor={trBeltColor} setTrBeltColor={setTrBeltColor}
              testIdPrefix="sweater-trouser"
            />
          </_Section>

          {/* ── Shoes ────────────────────────────────────────── */}
          <_Section title="Shoes" testid="sweater-shoe-section">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-wider text-[#52525B]">Style</p>
                <div className="flex flex-wrap gap-2">
                  {SHOE_STYLES.map(s => (
                    <_Chip
                      key={s.id}
                      selected={shoeStyle === s.id}
                      onClick={() => setShoeStyle(s.id)}
                      label={s.label}
                      testid={`sweater-shoe-style-${s.id}`}
                    />
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-wider text-[#52525B]">Colour</p>
                <div className="flex flex-wrap gap-2">
                  {SHOE_COLOURS.map(c => (
                    <_Chip
                      key={c.id}
                      selected={shoeColour === c.id}
                      onClick={() => setShoeColour(c.id)}
                      hex={c.hex}
                      label={c.label}
                      testid={`sweater-shoe-color-${c.id.replace(/\s+/g, '-').toLowerCase()}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </_Section>

          {/* ── Submit ───────────────────────────────────────── */}
          <div className="pt-4 border-t border-[#27272A] flex flex-col gap-3">
            {!hasClientImage && (
              <div
                className="px-4 py-3 border border-[#4F2F1F] bg-[#2A1A0F] flex items-start gap-3"
                data-testid="sweater-no-client-photo-warning"
              >
                <div className="text-[#D4A87A] text-sm mt-0.5">⚠</div>
                <div className="flex-1">
                  <p className="text-[11px] uppercase tracking-widest text-[#D4A87A] font-semibold">
                    Client photo required
                  </p>
                  <p className="text-[11px] text-[#A1A1AA] mt-1 leading-relaxed">
                    The render needs the client&apos;s face reference. Close this modal and upload a photo in the <em>Client Photo</em> panel, then click Render again.
                  </p>
                </div>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-end">
              <Button
                variant="ghost"
                onClick={onClose}
                disabled={generating}
                className="text-[#A1A1AA] hover:bg-[#1A1A1E] rounded-none"
                data-testid="sweater-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={generating || !hasClientImage}
                title={!hasClientImage ? 'Upload a client photo first — the render needs a face reference' : undefined}
                className="rounded-none uppercase tracking-wider text-xs px-6 py-5 disabled:opacity-40"
                style={{ backgroundColor: 'hsl(var(--accent))', color: '#0A0A0C' }}
                data-testid="sweater-submit"
              >
                {generating
                  ? (<><Loader2 className="w-4 h-4 spinner mr-2" /> Rendering…</>)
                  : (<><Sparkles className="w-4 h-4 mr-2" /> Render Look</>)}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
